/**
 * Wire types + pure helpers for `GET /flows/external-refs?...`. The web run
 * surfaces use these to find which fields of a catalog schema (an event's
 * payload, an action's params) are external references (fieldType
 * `'<provider>:<kind>'`, stamped by `externalRefIdSchema`) and which ids a
 * concrete payload carries. Type-only re-exports keep server route and web
 * client on one contract.
 */
import { parseExternalRefFieldType } from '@reflex/plugin-api/external-refs';

export type { ExternalRefLookupResult } from '@reflex/plugin-api/services';
export type { ExternalRefProfile } from '@reflex/plugin-api/external-refs';

/**
 * Most ids a single lookup accepts. Payloads carry a handful of reference
 * fields; the cap bounds provider-API fan-out for a hostile or buggy
 * caller. Shared so the web slices before requesting instead of earning a
 * 400.
 */
export const EXTERNAL_REFS_MAX_IDS = 25;

/** 'slack' → 'Slack', for badge/popover copy about the provider. */
export function providerDisplayLabel(provider: string): string {
  return provider.length === 0 ? provider : provider.charAt(0).toUpperCase() + provider.slice(1);
}

/** One schema field that references entities of an external provider. */
export interface ExternalRefField {
  /** Top-level property name in the payload ('userId', 'channelId'). */
  key: string;
  /** The full `'<provider>:<kind>'` fieldType keying its resolver. */
  fieldType: string;
  /** Provider namespace parsed from the fieldType. */
  provider: string;
  /** Entity kind parsed from the fieldType ('user', 'channel', 'team'). */
  kind: string;
  /** Friendly display label from the schema's `title` ('Assignee'). */
  title?: string;
}

/**
 * Scan a JSON Schema's top-level properties for external-ref fields.
 * Tolerant of arbitrary schemas: anything without a string `fieldType` in
 * the `<provider>:<kind>` namespace is skipped.
 */
export function externalRefFieldsFromSchema(
  schema: Record<string, unknown> | null | undefined,
): ExternalRefField[] {
  const properties = schema?.properties;
  if (!properties || typeof properties !== 'object') return [];
  const fields: ExternalRefField[] = [];
  for (const [key, prop] of Object.entries(properties as Record<string, unknown>)) {
    if (!prop || typeof prop !== 'object') continue;
    const { fieldType, title, items } = prop as Record<string, unknown>;
    // Array fields may carry the annotation on their element schema
    // (`z.array(GitHubLoginSchema)`) rather than the array property.
    const itemFieldType =
      items && typeof items === 'object' ? (items as Record<string, unknown>).fieldType : undefined;
    const effective = typeof fieldType === 'string' ? fieldType : itemFieldType;
    if (typeof effective !== 'string') continue;
    const parsed = parseExternalRefFieldType(effective);
    if (parsed) {
      fields.push({
        key,
        fieldType: effective,
        ...parsed,
        ...(typeof title === 'string' ? { title } : {}),
      });
    }
  }
  return fields;
}

/**
 * Same scan over a `FlowIOField[]` list (an action's declared outputs), so
 * run surfaces can badge the reference-typed fields of an action's result.
 */
export function externalRefFieldsFromIO(
  io: Array<{ key: string; fieldType?: string; description?: string }> | null | undefined,
): ExternalRefField[] {
  const fields: ExternalRefField[] = [];
  for (const entry of io ?? []) {
    if (typeof entry.fieldType !== 'string') continue;
    const parsed = parseExternalRefFieldType(entry.fieldType);
    if (parsed) fields.push({ key: entry.key, fieldType: entry.fieldType, ...parsed });
  }
  return fields;
}

/**
 * Collect the ids a payload actually carries for the given fields, grouped
 * by fieldType and deduplicated. String-array values are supported for
 * list-shaped fields (e.g. reviewer ids); anything non-string is ignored —
 * payloads are webhook data, not trusted shapes.
 */
export function collectExternalRefIds(
  fields: ExternalRefField[],
  payload: Record<string, unknown> | null | undefined,
): Map<string, string[]> {
  const byFieldType = new Map<string, Set<string>>();
  if (payload) {
    for (const { key, fieldType } of fields) {
      const value = payload[key];
      const candidates = Array.isArray(value) ? value : [value];
      for (const candidate of candidates) {
        if (typeof candidate !== 'string' || candidate.length === 0) continue;
        let ids = byFieldType.get(fieldType);
        if (!ids) byFieldType.set(fieldType, (ids = new Set()));
        ids.add(candidate);
      }
    }
  }
  return new Map([...byFieldType].map(([fieldType, ids]) => [fieldType, [...ids].sort()]));
}
